// Mark and ink descriptors. The *schemas* are shared because both backends
// offer the same vocabulary; the implementations differ and live in
// backends/<name>/. Anything a backend cannot honour it ignores.

export const markParams = [
  { key: 'geometry', label: 'geometry', tier: 'structural', type: 'enum', default: 'dot',
    options: [
      { value: 'dot', label: 'dot' },
      { value: 'tick', label: 'tick' },
      { value: 'polygon', label: 'polygon' },
      // Strokes the substrate's own chains rather than drawing one shape per
      // sample. Needs a substrate that has an ordering — a particle cloud
      // has none, and draws nothing.
      { value: 'path', label: 'path' },
      { value: 'glyph', label: 'glyph' },
    ] },
  // Up to 14 because a mark is sized in pixels while the lattice scales with
  // the frame, so no transform trick makes a mark large relative to its
  // neighbours — chapter 49's Flower of Life needs circles that reach the next
  // lattice point, which is a scale of about 13. The cost is a coarser slider
  // for the ordinary 0.5 to 3 range.
  { key: 'scale', label: 'scale', tier: 'continuous', min: 0.2, max: 14, step: 0.05, default: 1, format: 'x' },
  // One polygon beats a dozen named shapes: 3 is a triangle, 4 a diamond,
  // 6 a hexagon, 12 reads as a circle.
  { key: 'sides', label: 'sides', tier: 'cosmetic', min: 3, max: 12, step: 1, default: 4 },
  // Constant offset in turns, so a quarter turn is 0.25 rather than 1.5708.
  // This is the mark's own orientation; stage 03 supplies the per-point angle
  // on top of it. A 4-gon at 0 is a diamond, at 0.125 a square.
  { key: 'turn', label: 'turn', tier: 'cosmetic', min: 0, max: 1, step: 0.005, default: 0, format: 'turn' },
  // How a value picks a character. `sign` is chapter 42's rule and was the
  // only one for a long time — three slots, rising, falling and balanced —
  // which quietly made the glyph set a three-character field whatever you
  // typed into it. `ramp` spends the whole set, mapping the value across it
  // the way an ASCII density ramp does, which is what chapter 70 needs for
  // its eighteen braille characters.
  { key: 'glyphMode', label: 'glyph map', tier: 'cosmetic', type: 'enum', default: 'sign',
    options: [
      { value: 'sign', label: 'sign' },
      { value: 'ramp', label: 'ramp' },
    ] },
  { key: 'glyphs', label: 'glyph set', tier: 'cosmetic', type: 'text', default: '/\\|' },
]

export const inkParams = [
  { key: 'gain', label: 'gain', tier: 'continuous', min: 0.1, max: 5, step: 0.05, default: 1.6 },
  { key: 'weight', label: 'weight', tier: 'cosmetic', min: 0.3, max: 4, step: 0.1, default: 0.9 },
  // Ink where the field crosses evenly spaced levels rather than where it is
  // strong: contour lines. Chapters 37 and 56 both run marching squares over
  // a wave field at five levels, and tracing an isoline is not something a
  // bag of samples can do — but inking the samples *near* one is, and at any
  // reasonable density it reads the same.
  //
  // This generalises invert rather than replacing it: bands 0.5 puts its only
  // level at zero, which is exactly what invert 1 does. invert stays because
  // it blends continuously between the peaks and the nodes, which bands
  // cannot; bands stays because five levels is not one.
  { key: 'bands', label: 'contours', tier: 'continuous', min: 0, max: 8, step: 0.5, default: 0,
    hint: 'levels per unit of value · 0 = off' },
  // Ink the nodes instead of the peaks. Several chapters render where a field
  // cancels rather than where it is strongest — chapter 15 draws only
  // |amplitude| < 0.6 and fades it out as the amplitude grows — and the ink
  // stage is where that belongs, since it is a question about how the value is
  // laid down, not about the value. 0 is the ordinary reading; it blends.
  { key: 'invert', label: 'invert', tier: 'continuous', min: 0, max: 1, step: 0.02, default: 0,
    hint: 'ink where the field cancels' },
  // Transparency as its own control, on top of the alpha the field drives.
  // gain says how strongly a value inks; this says how present the layer is
  // at all, which is what you want when stacking layers over each other.
  { key: 'opacity', label: 'opacity', tier: 'cosmetic', min: 0, max: 1, step: 0.01, default: 1 },
  // Saturation 0 defers to the theme's ink; above 0 the layer states its own.
  // `wrap` marks hue as circular, so a blend or a modulation takes the short
  // way round from 350 to 10 rather than travelling the whole wheel.
  { key: 'hue', label: 'hue', tier: 'cosmetic', min: 0, max: 360, step: 1, default: 210, wrap: 360 },
  { key: 'saturation', label: 'saturation', tier: 'cosmetic', min: 0, max: 1, step: 0.01, default: 0,
    hint: '0 follows the theme' },
  { key: 'lightness', label: 'lightness', tier: 'cosmetic', min: 0, max: 1, step: 0.01, default: 0.42 },
  // Hard clear vs alpha wash. 11 chapters use the wash to leave trails.
  { key: 'persistence', label: 'persistence', tier: 'cosmetic', min: 0, max: 0.96, step: 0.02, default: 0 },
]

// Stage 06 is the only stage that knows the piece has depth. `tilt` lifts the
// height field off the plane; `perspective` is a weak divide — enough to read
// depth, not a pretence at being a real camera. Both default to 0, so a piece
// written before z existed renders exactly as it did.
export const frameParams = [
  { key: 'zoom', label: 'zoom', tier: 'continuous', min: 0.4, max: 2.5, step: 0.01, default: 1, format: 'x' },
  { key: 'rotation', label: 'rotation', tier: 'continuous', min: -3.14, max: 3.14, step: 0.01, default: 0 },
  // The whole piece turning about its height axis — chapter 07's
  // `pineCone.rotation.y = time * 0.3`. This is a revolve, not a spin: it
  // belongs in 06 because it is how we look at the piece, not how a mark
  // responds. Compare motion.spin in 03, which turns each mark in place.
  { key: 'orbit', label: 'orbit', tier: 'continuous', min: -2, max: 2, step: 0.01, default: 0,
    hint: 'revolve the whole piece · radians per unit time · needs tilt to read as depth' },
  { key: 'tilt', label: 'tilt', tier: 'continuous', min: 0, max: 1.2, step: 0.01, default: 0, hint: 'x-axis, radians' },
  { key: 'perspective', label: 'perspective', tier: 'continuous', min: 0, max: 1, step: 0.02, default: 0, hint: '0 = orthographic' },
  // The two details that make chapter 07 feel alive rather than mechanical:
  //   rotation.x = sin(t * 0.5) * 0.05   rotation.z = cos(t * 0.7) * 0.03
  //   scale      = 1 + sin(t * 0.5) * 0.02
  // Wobble drives both rotation axes at that 0.6 ratio and those rates, so one
  // slider reproduces the pair; breathe is the scale pulse. Both want to stay
  // small — this is a breath, not a bounce.
  { key: 'wobble', label: 'wobble', tier: 'continuous', min: 0, max: 0.3, step: 0.005, default: 0,
    hint: 'slow drift on both rotation axes' },
  { key: 'breathe', label: 'breathe', tier: 'continuous', min: 0, max: 0.2, step: 0.005, default: 0,
    hint: 'scale pulse' },
  { key: 'vignette', label: 'vignette', tier: 'cosmetic', min: 0, max: 1, step: 0.02, default: 0 },
  // The ground the piece sits on. Same rule as the ink: saturation 0 defers to
  // the theme's paper, so a piece follows light and dark mode until it says
  // otherwise. It belongs to the frame because it is part of how the piece is
  // looked at rather than how any mark is laid down.
  { key: 'groundHue', label: 'ground hue', tier: 'cosmetic', min: 0, max: 360, step: 1, default: 40, wrap: 360 },
  { key: 'groundSaturation', label: 'ground sat', tier: 'cosmetic', min: 0, max: 1, step: 0.01, default: 0,
    hint: '0 follows the theme' },
  { key: 'groundLightness', label: 'ground light', tier: 'cosmetic', min: 0, max: 1, step: 0.01, default: 0.93 },
]

// A dot is rotationally symmetric, a glyph is drawn by the text engine, and a
// path takes its direction from the chain rather than from the field, so none
// of them can show an angle. Everything else can.
const ORIENTED = new Set(['tick', 'polygon'])
export const isOriented = (mark) => ORIENTED.has(mark.geometry)

// The gradient costs four extra field samples per point — by far the most
// expensive thing stage 03 can be asked to do. Two things can want it, and it
// is only paid for when one of them actually does: an orientable mark aligned
// to the flow, or an in-plane shift pushed up the field's slope.
export function needsGradient(mark, motion) {
  const forAngle = isOriented(mark) && motion.align === 'flow' && motion.twist !== 0
  const forShift = motion.shift !== 0 && motion.axis === 'gradient'
  return forAngle || forShift
}
